import { Injectable } from '@angular/core';
import { Mutex } from '../../shared/Mutex';
import { AlertService, MessageSeverity } from '../alert.service';
import { IActionConfig } from '../app-initializer/app-initializer.service';
import { ConfigsService } from '../configs/configs.service';
import { PluginEndpointService } from '../plugin/plugin.endpoint.service';
import { MemberService } from "../member/member.service";

@Injectable({ providedIn: 'root' })
export class ActionService {

    collectionMutex = new Mutex();

    private ActionKey: string = "ACTION_CONFIG";

    private actions: any[] = [];

    constructor(private configsService: ConfigsService,
        private pluginService: PluginEndpointService,
        private alertService: AlertService,
        private memberService: MemberService) { }

    async invoke(type: string, action: string, metadata?: any): Promise<any> {

        metadata = this.prepareMetadata(type, metadata);

        const hash = window.btoa(encodeURIComponent(JSON.stringify(metadata)));

        let actionConfig = this.actions.find(a => a.hash == hash) as IActionConfig;

        if (actionConfig != null) {
            console.log(`Using existing config for Type(${type})`);
            return await this.sendRequest(actionConfig, action, metadata);
        }

        if (type && typeof type == "string") {
            actionConfig = await this.initializeV2<IActionConfig>(type);

            if (!actionConfig) {
                return null;
            }

            return await this.sendRequest(actionConfig, action, metadata);
        }

        return null;
    }

    async invokeAction(actionConfig: IActionConfig, action: string, metadata: any): Promise<any> {

        if (!actionConfig) {
            return null;
        }

        return await this.sendRequest(actionConfig, action, metadata);
    }

    async invokeV2<T>(type: string, action: string, metadata?: any): Promise<T> {
        return (await this.invoke(type, action, metadata)) as T;
    }

    getActionConfigMetadata<T>(type?: string): T {
        return this.actions[0] as T;
    }

    public async initialize(type: string, metadata?: any): Promise<boolean> {

        const result = this.initializeV2<any>(type, metadata);

        return result != null;
    }

    public async initializeV2<T>(type: string, metadata?: any): Promise<T> {

        const unlock = await this.collectionMutex.lock();

        try {

            metadata = this.prepareMetadata(type, metadata);

            const hash = window.btoa(encodeURIComponent(JSON.stringify(metadata)));

            let actionConfig = this.actions.find(a => a.hash == hash) as IActionConfig;

            if (actionConfig != null) {
                return actionConfig as T;
            }

            if (!metadata) {
                metadata = {};
            }

            metadata.ThrowOnError = false;

            const configs = await this.configsService.firstOrDefault(this.ActionKey, metadata);
            try {
                if (configs.errors.length == 0) {
                    const config = JSON.parse(configs.value);
                    this.actions.push({ ...config, hash: hash });
                    // console.log(`Adding config for Type(${type})`);
                    return config as T;
                } else {
                    this.alertService.showStickyMessage('Configuration Error', `Action Information not found for: ${this.ActionKey} ${type}`, MessageSeverity.error, "Information not found");
                    return null as T;
                }
            } catch (error) {
                console.log("Error initializing action", error, configs?.value);
                return null as T;
            }
        }
        finally {
            unlock();
        }
    }

    private async sendRequest(actionConfig: IActionConfig, action: string, metadata: any) {

        return await this.pluginService.sendPluginRequest({
            packageName: actionConfig.packageName,
            version: actionConfig.packageVersion,
            action: action,
            request: metadata,
        });
    }

    private prepareMetadata(actionType: string, metadata?: any) {

        if (!metadata) {
            metadata = {};
        }

        metadata.Type = actionType;
        metadata.ThrowOnError = false;

        if (!metadata.tenantId) {
            metadata.TenantId ??= this.memberService?.tenantIdSync;
        }

        return metadata;
    }
}

export class IActionHandlerResponse {
    error?: string;
    stackTrace?: string;
}
